import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import { orderTrackingPageTabs } from '@/configuration/pages/ConfigurationPage/OrderTrackingPageConfiguration/constants'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { duplicateCriterionIdPrefix, initCriterion, newCriterionId } from './constants'
import {
  transformRequestCreateVisibilityCriterion,
  transformRequestEditVisibilityCriterion,
  transformResponseGetVisibilityCriterion,
} from './transformers'

const visibilityCriteriaTabRoute = `${routes.configurationPage.orderTrackingPage.config}?tab=${orderTrackingPageTabs.visibilityCriteria}`

const useVisibilityCriterion = ({ id, storeId, setToastInfo, storeCurrencyCode }) => {
  const isDuplicate = id.startsWith(duplicateCriterionIdPrefix)
  const isNew = id === newCriterionId
  const criterionId = isDuplicate ? id.replace(duplicateCriterionIdPrefix, '') : id

  const { t } = useTranslation()
  const navigate = useNavigate()

  const [criterion, setCriterion] = useState(initCriterion)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState([])
  const originalCriterion = useRef(initCriterion)

  const { fetchData: fetchVisibilityCriterion, loading: fetchingVisibilityCriterion } = useFetch(
    configurationServices.getVisibilityCriterion,
    { doInitialRequest: false, transformResponse: transformResponseGetVisibilityCriterion }
  )

  const { fetchData: createVisibilityCriterion, loading: creatingVisibilityCriterion } = useFetch(
    configurationServices.createVisibilityCriterion,
    { doInitialRequest: false, transformRequest: transformRequestCreateVisibilityCriterion }
  )

  const { fetchData: editVisibilityCriterion, loading: editingVisibilityCriterion } = useFetch(
    configurationServices.editVisibilityCriterion,
    { doInitialRequest: false, transformRequest: transformRequestEditVisibilityCriterion }
  )

  const loadCriterion = useCallback(async () => {
    if (isNew) return

    try {
      const data = await fetchVisibilityCriterion({ storeId, criterionId })

      originalCriterion.current = data
      setCriterion(data)

      if (isDuplicate) setHasChanges(true)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }, [isNew, isDuplicate, fetchVisibilityCriterion, storeId, criterionId, setToastInfo])

  useEffect(() => {
    loadCriterion()
  }, [loadCriterion])

  const onChangeCriterion = paramsEdited => {
    setCriterion(prev => ({ ...prev, ...paramsEdited }))
    setHasChanges(true)
  }

  const revertChanges = () => {
    if (isNew || isDuplicate) {
      navigate(visibilityCriteriaTabRoute)

      return
    }

    setCriterion(originalCriterion.current)
    setErrors([])
    setHasChanges(false)
  }

  const saveChanges = async () => {
    try {
      if (isNew || isDuplicate) {
        await createVisibilityCriterion({ storeId, criterion, defaultCurrency: storeCurrencyCode })
      } else {
        await editVisibilityCriterion({ storeId, criterionId, criterion, defaultCurrency: storeCurrencyCode })
      }

      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      navigate(visibilityCriteriaTabRoute)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return {
    criterion,
    fetchingVisibilityCriterion,
    saving: creatingVisibilityCriterion || editingVisibilityCriterion,
    hasChanges,
    errors,
    setErrors,
    onChangeCriterion,
    revertChanges,
    saveChanges,
    isNew,
    isDuplicate,
  }
}

export default useVisibilityCriterion
